import { useState } from 'react';
import { Link } from 'react-router-dom';
import { Clock, FileText, AlertCircle, CheckCircle, Loader, XCircle, StopCircle, Trash2, Eye, Glasses } from 'lucide-react';
import { generateClient, GraphQLResult } from 'aws-amplify/api';
import { Scan } from '../../types';
import { formatDateTime, formatDuration } from '../../utils/formatting';
import { CANCEL_SCAN, ARCHIVE_SCAN_TO_TRASH } from '../../graphql/mutations';
import { GET_PDF_DOWNLOAD_URL } from '../../graphql/queries';

const client = generateClient();

interface ScanCardProps {
  scan: Scan;
  onCancelled?: () => void;
  onTrashed?: () => void;
}

export function ScanCard({ scan, onCancelled, onTrashed }: ScanCardProps) {
  const [isCancelling, setIsCancelling] = useState(false);
  const [isArchiving, setIsArchiving] = useState(false);
  const [isDownloading, setIsDownloading] = useState(false);

  /**
   * Ask for a fresh signed URL rather than following the one stored on the scan.
   *
   * `scan.pdfReportUrl` is signed when the report is generated, using the Lambda's
   * temporary credentials — so it dies with that session, hours later, long before the
   * seven days it asks for, and clicking it returns S3's ExpiredToken. The scan detail
   * page has always minted a fresh URL on demand; this is the same call.
   */
  const handleDownloadPdf = async () => {
    setIsDownloading(true);
    try {
      const response = (await client.graphql({
        query: GET_PDF_DOWNLOAD_URL,
        variables: { siteId: scan.siteId, scanId: scan.scanId },
      })) as GraphQLResult<{ getPdfDownloadUrl: { downloadUrl: string } | null }>;

      const downloadUrl = response.data?.getPdfDownloadUrl?.downloadUrl;
      if (downloadUrl) window.open(downloadUrl, '_blank', 'noopener');
    } catch (err) {
      console.error('Failed to get PDF download link:', err);
    } finally {
      setIsDownloading(false);
    }
  };
  // Normalize status to lowercase for comparison (backend sends uppercase)
  const status = scan.status.toLowerCase();
  const isInProgress = ['pending', 'crawling', 'scanning', 'aggregating', 'generating_report'].includes(status);
  const isCompleted = status === 'completed';
  const isFailed = status === 'failed';

  const getStatusIcon = () => {
    switch (status) {
      case 'completed':
        return <CheckCircle className="text-status-success" size={20} />;
      case 'failed':
        return <XCircle className="text-status-error" size={20} />;
      case 'cancelled':
        return <XCircle className="text-gray-medium" size={20} />;
      default:
        return <Loader className="text-coral animate-spin" size={20} />;
    }
  };

  const getStatusText = () => {
    switch (status) {
      case 'pending':
        return 'Pending...';
      case 'crawling':
        return `Crawling... (${scan.progress?.pagesDiscovered || 0} pages found)`;
      case 'scanning':
        return `Scanning... (${scan.progress?.pagesScanned || 0}/${scan.progress?.pagesDiscovered || 0})`;
      case 'aggregating':
        return 'Processing results...';
      case 'generating_report':
        return 'Generating report...';
      case 'completed':
        return 'Completed';
      case 'failed':
        return 'Failed';
      case 'cancelled':
        return 'Cancelled';
      default:
        return scan.status;
    }
  };

  const duration = scan.completedAt
    ? formatDuration(new Date(scan.completedAt).getTime() - new Date(scan.startedAt).getTime())
    : null;

  const handleCancel = async () => {
    if (isCancelling) return;

    setIsCancelling(true);
    try {
      await client.graphql({
        query: CANCEL_SCAN,
        variables: { siteId: scan.siteId, scanId: scan.scanId },
      });
      onCancelled?.();
    } catch (error) {
      console.error('Failed to cancel scan:', error);
    } finally {
      setIsCancelling(false);
    }
  };

  const handleArchiveToTrash = async () => {
    if (isArchiving) return;

    setIsArchiving(true);
    try {
      await client.graphql({
        query: ARCHIVE_SCAN_TO_TRASH,
        variables: { siteId: scan.siteId, scanId: scan.scanId },
      });
      onTrashed?.();
    } catch (error) {
      console.error('Failed to archive scan:', error);
    } finally {
      setIsArchiving(false);
    }
  };

  const isCancelled = status === 'cancelled';
  const canBeArchived = isCompleted || isFailed || isCancelled;

  return (
    <div className="card-hover">
      <div className="flex items-start gap-4">
        {/* Status Icon */}
        <div className="flex-shrink-0 mt-1">{getStatusIcon()}</div>

        {/* Content */}
        <div className="flex-1 min-w-0">
          <div className="flex items-center gap-3 mb-2">
            <span className="text-body-sm text-gray-medium">
              {formatDateTime(scan.startedAt)}
            </span>
            {duration && (
              <span className="text-caption text-gray-medium flex items-center gap-1">
                <Clock size={12} />
                {duration}
              </span>
            )}
          </div>

          <div className="text-body font-medium mb-1">{getStatusText()}</div>

          {/* Config Summary */}
          <div className="flex flex-wrap gap-2 text-caption text-gray-medium">
            <span>WCAG {scan.config?.wcagVersion} {scan.config?.wcagLevel}</span>
            <span>•</span>
            <span>{scan.config?.scanTypes?.length || 0} scan types</span>
            <span>•</span>
            <span>{scan.progress?.pagesScanned || 0} pages</span>
          </div>

          {/* Error Message */}
          {isFailed && scan.error && (
            <div className="mt-2 flex items-center gap-2 text-body-sm text-status-error">
              <AlertCircle size={14} />
              {scan.error}
            </div>
          )}
        </div>

        {/* Results Summary */}
        {isCompleted && scan.results && (
          <div className="flex-shrink-0 flex items-center gap-4">
            {/* View Results Link */}
            <Link
              to={`/sites/${scan.siteId}/scans/${scan.scanId}`}
              className="btn-secondary text-body-sm py-2 px-4"
            >
              View Results
            </Link>
          </div>
        )}

        {/* PDF Download */}
        {isCompleted && scan.pdfReportUrl && (
          <button
            onClick={handleDownloadPdf}
            disabled={isDownloading}
            className="flex-shrink-0 p-2 text-gray-medium hover:text-coral transition-colors disabled:opacity-50"
            title="Download PDF Report"
          >
            {isDownloading ? <Loader size={20} className="animate-spin" /> : <FileText size={20} />}
          </button>
        )}

        {/* View Progress Link for In-Progress Scans */}
        {isInProgress && (
          <Link
            to={`/sites/${scan.siteId}/scans/${scan.scanId}`}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 text-coral hover:bg-coral/10 rounded-card transition-colors"
          >
            <Eye size={18} />
            <span className="text-body-sm font-medium">View Progress</span>
          </Link>
        )}

        {/* Cancel Button for In-Progress Scans */}
        {isInProgress && (
          <button
            onClick={(e) => { e.preventDefault(); handleCancel(); }}
            disabled={isCancelling}
            className="flex-shrink-0 flex items-center gap-2 px-3 py-2 text-status-error hover:bg-status-error/10 rounded-card transition-colors disabled:opacity-50"
            title="Cancel Scan"
          >
            {isCancelling ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <StopCircle size={18} />
            )}
            <span className="text-body-sm font-medium">Cancel</span>
          </button>
        )}

        {/* Trash Button for Finished Scans */}
        {canBeArchived && (
          <button
            onClick={handleArchiveToTrash}
            disabled={isArchiving}
            className="flex-shrink-0 p-2 text-gray-medium hover:text-status-error hover:bg-status-error/10 rounded-card transition-colors disabled:opacity-50"
            title="Move to Trash"
          >
            {isArchiving ? (
              <Loader className="animate-spin" size={18} />
            ) : (
              <Trash2 size={18} />
            )}
          </button>
        )}
      </div>

      {/* Progress Bar — only during Scanning, when the denominator (pagesDiscovered) is final */}
      {status === 'scanning' && (scan.progress?.pagesDiscovered || 0) > 0 && (
        <div className="mt-4 pt-4 border-t border-gray-light">
          <div className="flex justify-between text-caption text-gray-medium mb-1">
            <span>Progress</span>
            <span>
              {scan.progress?.pagesScanned || 0} / {scan.progress?.pagesDiscovered || 0} pages
            </span>
          </div>
          <div className="h-2 bg-gray-light rounded-full overflow-hidden">
            <div
              className="h-full bg-coral transition-all duration-300"
              style={{
                width: `${((scan.progress?.pagesScanned || 0) / (scan.progress?.pagesDiscovered || 1)) * 100}%`,
              }}
            />
          </div>
        </div>
      )}

      {/* Issue Summary for Completed Scans */}
      {isCompleted && scan.results && (
        <div className="mt-4 pt-4 border-t border-gray-light flex gap-4">
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-severity-critical" />
            <span className="text-body-sm">{(scan.results.uniqueIssues ?? scan.results.totalIssues).critical} Critical</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-severity-serious" />
            <span className="text-body-sm">{(scan.results.uniqueIssues ?? scan.results.totalIssues).serious} Serious</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-severity-moderate" />
            <span className="text-body-sm">{(scan.results.uniqueIssues ?? scan.results.totalIssues).moderate} Moderate</span>
          </div>
          <div className="flex items-center gap-1.5">
            <span className="w-2 h-2 rounded-full bg-severity-minor" />
            <span className="text-body-sm">{(scan.results.uniqueIssues ?? scan.results.totalIssues).minor} Minor</span>
          </div>
          {scan.results.manualReviewCount ? (
            <div className="flex items-center gap-1.5" title="Needs a human check — excluded from the score">
              <Glasses size={12} className="text-gray-medium" />
              <span className="text-body-sm">{scan.results.manualReviewCount} Manual review</span>
            </div>
          ) : null}
        </div>
      )}
    </div>
  );
}
